import { useContext, useEffect } from 'react';
import { ApiContext } from '../context/ApiContext';
import apiCall, { isUserAuthenticated } from '../utils/apiCall';
import { silentRefreshWithBackoff as utilsSilentRefreshWithBackoff, monitorTokenExpiration as utilsMonitorTokenExpiration } from '../utils/authUtils';

const useAuth = () => {
    const apiUrl = useContext(ApiContext);

    const login = async (email, password) => {
        const loginUrl = apiUrl ? `${apiUrl}/api/v1/login` : '/api/v1/login';
        
        try {
            const response = await apiCall(loginUrl, 'POST', { email, password });

            if (!response.ok) {
                const errorData = await response.json();
                localStorage.setItem('isLoggedIn', 'false');
                localStorage.removeItem('tokenExpiresAt');
                throw new Error(errorData.message || 'Login failed');
            }

            const data = await response.json();
            console.log('Data:', data);

            if (data.accessTokenExpiresAt) {
                localStorage.setItem('isLoggedIn', 'true');
                localStorage.setItem('tokenExpiresAt', data.accessTokenExpiresAt);
            } else {
                localStorage.remove('tokenExpiresAt');
            }

            // apiCall handles setting isLoggedIn=true on success
            return { success: true, user: data };
        } catch (error) {
            // Enhanced error handling
            console.error('Login error:', error);
            localStorage.setItem('isLoggedIn', 'false');
            localStorage.removeItem('tokenExpiresAt');
            
            // Provide more specific error messages
            if (error.message.includes('network')) {
                throw new Error('Network error. Please check your connection.');
            } else if (error.message.includes('credentials')) {
                throw new Error('Invalid email or password.');
            } else {
                throw new Error(error.message || 'Login failed. Please try again.');
            }
        }
    };

    const logout = async () => {
        const logoutUrl = apiUrl ? `${apiUrl}/api/v1/logout` : '/api/v1/logout';
        
        try {
            const response = await apiCall(logoutUrl, 'POST', {});

            if (!response.ok) {
                // Even if logout fails, we should still clear local state
                localStorage.setItem('isLoggedIn', 'false');
                localStorage.removeItem('tokenExpiresAt');
                clearAuthCookies();
                throw new Error('Logout failed');
            }

            // Ensure cookies are cleared on successful logout
            clearAuthCookies();
            localStorage.removeItem('tokenExpiresAt');
            // apiCall handles setting isLoggedIn=false on successful logout
            return { success: true };
        } catch (error) {
            // Enhanced error handling for logout
            console.error('Logout error:', error);
            localStorage.setItem('isLoggedIn', 'false');
            localStorage.removeItem('tokenExpiresAt');
            clearAuthCookies();
            
            // Provide user-friendly error message
            throw new Error('Logout failed. You have been logged out locally.');
        }
    };

    // Enhanced cookie cleanup that matches backend exactly
    const clearAuthCookies = () => {
        // Clear access token cookie with multiple paths
        document.cookie = 'access_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure; SameSite=Lax';
        document.cookie = 'access_token=; Path=/api; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure; SameSite=Lax';
        
        // Clear refresh token cookie with multiple paths
        document.cookie = 'refresh_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure; SameSite=Lax';
        document.cookie = 'refresh_token=; Path=/api; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure; SameSite=Lax';
        
        // Clear any session cookies
        document.cookie = 'connect.sid=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Secure; SameSite=Lax';
    };

    const refreshSession = async () => {
        const refreshUrl = apiUrl ? `${apiUrl}/api/v1/refresh-token` : '/api/v1/refresh-token';
        
        try {
            const response = await apiCall(refreshUrl, 'POST', {});

            if (!response.ok) {
                localStorage.setItem('isLoggedIn', 'false');
                localStorage.removeItem('tokenExpiresAt');
                throw new Error('Session refresh failed');
            }

            const data = await response.json();
            console.log('Data:', data);

            if (data.accessTokenExpiresAt) {
                localStorage.setItem('isLoggedIn', 'true');
                localStorage.setItem('tokenExpiresAt', data.accessTokenExpiresAt);
            } else {
                localStorage.remove('tokenExpiresAt');
            }


            // apiCall handles setting isLoggedIn=true on success
            return { success: true };
        } catch (error) {
            console.error('Session refresh error:', error);
            localStorage.setItem('isLoggedIn', 'false');
            localStorage.removeItem('tokenExpiresAt');
            throw new Error('Your session has expired. Please log in again.');
        }
    };

    /**
     * Silent refresh with exponential backoff retry
     * @param {number} retryCount - Current retry attempt
     * @returns {Promise<boolean>} True if refresh succeeded
     */
    const silentRefreshWithBackoff = async (retryCount = 0) => {
        return utilsSilentRefreshWithBackoff(retryCount, refreshSession);
    };

    /**
     * Monitor token expiration and trigger refresh
     */
    const monitorTokenExpiration = async () => {
        return utilsMonitorTokenExpiration(refreshSession);
    };

    /**
     * Upload user profile image
     * @param {File} file - The image file to upload
     * @returns {Promise<Object>} - Upload result
     */
    const uploadProfileImage = async (file) => {
        try {
            const uploadUrl = apiUrl ? `${apiUrl}/api/v1/user/profile-image/upload` : '/api/v1/user/profile-image/upload';
            
            // Read file as ArrayBuffer and send as raw bytes
            const fileBuffer = await file.arrayBuffer();
            
            const response = await fetch(uploadUrl, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/octet-stream',
                    'X-Client-Type': 'web',
                    'X-Filename': file.name
                },
                body: fileBuffer
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || 'Failed to upload profile image');
            }
            
            const data = await response.json();
            return data;
            
        } catch (error) {
            console.error('Profile image upload error:', error);
            throw new Error(error.message || 'Failed to upload profile image');
        }
    };

    /**
     * Get current user's profile image URL
     * @returns {Promise<Object>} - Profile image data
     */
    const getProfileImage = async () => {
        try {
            const profileImageUrl = apiUrl ? `${apiUrl}/api/v1/user/profile-image` : '/api/v1/user/profile-image';
            
            const response = await fetch(profileImageUrl, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Client-Type': 'web'
                }
            });
            
            if (!response.ok) {
                if (response.status === 404) {
                    return { success: true, profileImageUrl: null };
                }
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || 'Failed to get profile image');
            }
            
            const data = await response.json();
            return data;
            
        } catch (error) {
            console.error('Get profile image error:', error);
            throw new Error(error.message || 'Failed to get profile image');
        }
    };

    /**
     * Remove user profile image
     * @returns {Promise<Object>} - Removal result
     */
    const removeProfileImage = async () => {
        try {
            const removeUrl = apiUrl ? `${apiUrl}/api/v1/user/profile-image` : '/api/v1/user/profile-image';
            
            const response = await fetch(removeUrl, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Client-Type': 'web'
                }
            });
            
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || 'Failed to remove profile image');
            }
            
            // DELETE endpoint returns no content (204) or empty body (200)
            // Return success object instead of trying to parse JSON
            return { success: true };
            
        } catch (error) {
            console.error('Remove profile image error:', error);
            throw new Error(error.message || 'Failed to remove profile image');
        }
    };

    const callApi = (url, method, body = {}, headers = {}) => {
        return apiCall(url, method, body, headers);
    };

    const checkAuthStatus = () => {
        return isUserAuthenticated();
    };

    // Discussion API methods
    const createDiscussion = async (discussionData) => {
        const discussionUrl = apiUrl ? `${apiUrl}/api/v1/discussion` : '/api/v1/discussion';
        const response = await apiCall(
            discussionUrl,
            'POST',
            discussionData,
            true
        );
        return response;
    };

    const getDiscussions = async () => {
        const discussionsUrl = apiUrl ? `${apiUrl}/api/v1/discussion/list` : '/api/v1/discussion/list';
        const response = await apiCall(
            discussionsUrl,
            'GET',
            null,
            true
        );
        return response;
    };

    const getDiscussion = async (discussionId) => {
        const discussionUrl = apiUrl ? `${apiUrl}/api/v1/discussion/${discussionId}` : `/api/v1/discussion/${discussionId}`;
        const response = await apiCall(
            discussionUrl,
            'GET',
            null,
            true
        );
        return response;
    };

    const getCommentsByParent = async (parentType, parentId) => {
        const commentsUrl = apiUrl ? `${apiUrl}/api/v1/content/${parentType}/${parentId}/comment/list` : `/api/v1/content/${parentType}/${parentId}/comment/list`;
        const response = await apiCall(
            commentsUrl,
            'GET',
            null,
            true
        );
        return response;
    };

    const getDiscussionsByParent = async (parentType, parentId) => {
        const discussionsUrl = apiUrl ? `${apiUrl}/api/v1/content/${parentType}/${parentId}/discussion/list` : `/api/v1/content/${parentType}/${parentId}/discussion/list`;
        const response = await apiCall(
            discussionsUrl,
            'GET',
            null,
            true
        );
        return response;
    };

    // Update createComment to support parentType
    const createComment = async (commentData) => {
        const commentUrl = apiUrl ? `${apiUrl}/api/v1/post/comment` : '/api/v1/post/comment';
        const response = await apiCall(
            commentUrl,
            'POST',
            commentData,
            true
        );
        return response;
    };

    // Set up periodic token monitoring
    useEffect(() => {
        if (localStorage.getItem('isDemoMode') === 'true') return;
        const interval = setInterval(monitorTokenExpiration, 60000); // Check every minute
        return () => clearInterval(interval);
    }, []);

    return { 
        login, 
        logout, 
        refreshSession, 
        uploadProfileImage,
        getProfileImage,
        removeProfileImage,
        callApi, 
        checkAuthStatus,
        silentRefreshWithBackoff, // Export for testing
        monitorTokenExpiration, // Export for testing
        // Discussion API methods
        createDiscussion,
        getDiscussions,
        getDiscussion,
        getCommentsByParent,
        getDiscussionsByParent,
        createComment
    };
};

export default useAuth;
